import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatIndentIncreaseRoundedIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import FormatIndentDecreaseRoundedIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const extractPlaceholders = (html) => {
  if (!html) return [];
  const matches = html.match(/\{[^}]+\}/g) || [];
  const unique = Array.from(new Set(matches.map((m) => m.trim()))).filter(
    Boolean,
  );
  return unique;
};

const CommunicationTemplateNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const editorRef = useRef(null);
  const subjectRef = useRef(null);

  const isEditMode = !!overlayData?.template?.id;

  // Edit lock for existing communication templates only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "communicationTemplate",
    isEditMode ? overlayData?.template?.id : null,
  );

  const [name, setName] = useState(overlayData?.template?.Name || "");
  const [description, setDescription] = useState(
    overlayData?.template?.Description || "",
  );
  const [channel, setChannel] = useState(
    overlayData?.template?.Channel || "Email",
  );
  const [type, setType] = useState(overlayData?.template?.Type || "Template");
  const [subject, setSubject] = useState(overlayData?.template?.Subject || "");
  const [bodyHtml, setBodyHtml] = useState(overlayData?.template?.Body || "");
  const [isActive, setIsActive] = useState(
    overlayData?.template?.IsActive !== undefined
      ? overlayData.template.IsActive
      : true,
  );
  const [saving, setSaving] = useState(false);
  const [lastFocused, setLastFocused] = useState("body");
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);

  usePageTitle(
    isEditMode ? "Edit Communication Template" : "New Communication Template",
  );

  // Initialize editor content once on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = overlayData?.template?.Body || "";
    }
    setBodyHtml(overlayData?.template?.Body || "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeOverlay = () => setOverlayName("");

  const getPlainText = (html) => {
    if (!html) return "";
    return html
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleBodyInput = (e) => {
    setBodyHtml(e.currentTarget.innerHTML);
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertPlaceholderInBody = (placeholder) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    // Use document.execCommand for broad browser support
    const successful = document.execCommand("insertText", false, placeholder);

    if (!successful) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(placeholder));

      // Move cursor to end of inserted text
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    setBodyHtml(el.innerHTML);
  };

  const insertPlaceholderInSubject = (placeholder) => {
    const input = subjectRef.current;
    if (!input) return;
    const start = input.selectionStart ?? subject.length;
    const end = input.selectionEnd ?? start;
    const next = `${subject.slice(0, start)}${placeholder}${subject.slice(
      end,
    )}`;
    setSubject(next);

    // Restore caret after state update
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + placeholder.length;
      input.setSelectionRange(pos, pos);
    });
  };

  const insertPlaceholder = (placeholder) => {
    if (lastFocused === "subject") {
      insertPlaceholderInSubject(placeholder);
    } else {
      insertPlaceholderInBody(placeholder);
    }
  };

  const handleOpenLinkDialog = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0));
    }

    openDialog({
      theme: theme,
      title: "Insert Hyperlink",
      message: "",
      customContent: (
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateLink();
              }
            }}
            autoFocus
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Cancel",
      primaryAction: "Insert Link",
      tertiaryOnClick: null,
      secondaryOnClick: () => {
        closeDialog();
        setLinkUrl("");
        setSavedSelection(null);
      },
      primaryOnClick: () => {
        handleCreateLink();
      },
    });
  };

  const handleCloseLinkDialog = () => {
    closeDialog();
    setLinkUrl("");
    setSavedSelection(null);
  };

  const handleCreateLink = () => {
    if (!linkUrl.trim()) {
      showSnackbar("Please enter a URL", theme, "error");
      return;
    }

    const el = editorRef.current;
    if (!el) return;

    el.focus();

    // Restore the saved selection
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }

    // Create link with the provided URL
    let url = linkUrl.trim();
    // Add protocol if not present
    if (!url.match(/^https?:\/\//)) {
      url = "https://" + url;
    }

    document.execCommand("createLink", false, url);
    setBodyHtml(el.innerHTML);
    handleCloseLinkDialog();
  };

  const handleSave = async () => {
    if (saving || isLocked) {
      return;
    }

    if (!name.trim()) {
      showSnackbar("Name is required", theme, "error");
      return;
    }
    if (type === "Template" && channel === "Email" && !subject.trim()) {
      showSnackbar("Subject is required for Email templates", theme, "error");
      return;
    }
    if (!getPlainText(bodyHtml)) {
      showSnackbar("Body is required", theme, "error");
      return;
    }

    const payload = {
      Name: name.trim(),
      Description: description.trim() || null,
      Channel: channel,
      Type: type,
      Body: bodyHtml,
      IsActive: !!isActive,
    };

    if (type === "Template" && channel === "Email") {
      payload.Subject = subject.trim();
      payload.Placeholders = Array.from(
        new Set([
          ...extractPlaceholders(bodyHtml),
          ...extractPlaceholders(subject),
        ]),
      );
    } else if (type === "Template" && channel === "SMS") {
      payload.Subject = null;
      payload.Placeholders = extractPlaceholders(bodyHtml);
    } else {
      payload.Subject = null;
      payload.Placeholders = extractPlaceholders(bodyHtml);
    }

    if (!isEditMode) {
      if (!currentUser?.id) {
        showSnackbar("Missing creator information", theme, "error");
        return;
      }
      payload.CreatedBy = currentUser.id;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await api.put(
          `/communicationTemplates/${overlayData?.template?.id}`,
          payload,
        );
        showSnackbar("Template updated", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "CommunicationTemplate",
            recordId: overlayData?.template?.id,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        const response = await api.post("/communicationTemplates", payload);
        showSnackbar("Template created", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "CommunicationTemplate",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      console.error("Error saving template", err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving communication template",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: "90%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {isEditMode
                ? "Edit Communication Template"
                : "New Communication Template"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={closeOverlay}
                sx={{ borderRadius: "12px" }}
              >
                Discard
              </Button>
            )}

            <Tooltip
              title={isMobile && saving ? "Saving changes..." : ""}
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || isLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || isLocked}
                    sx={{ borderRadius: "12px" }}
                  >
                    Save
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Template Details
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Channel"
                required
                select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              >
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Type"
                required
                select
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="Template">Template</MenuItem>
                <MenuItem value="Modifier">Modifier</MenuItem>
              </TextField>
            </Grid>

            {type === "Template" && channel === "Email" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setLastFocused("subject")}
                  inputRef={subjectRef}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Body
                  </Typography>

                  {/* Formatting Toolbar */}
                  <Box
                    sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}
                  >
                    <Tooltip title="Bold">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("bold")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatBoldRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Italic">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("italic")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatItalicRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Underline">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("underline")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatUnderlinedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Bullet List">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("insertUnorderedList")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatListBulletedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Numbered List">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("insertOrderedList")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatListNumberedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Indent Right">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("indent")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatIndentIncreaseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Indent Left">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("outdent")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatIndentDecreaseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Insert Hyperlink">
                      <IconButton
                        size="small"
                        onClick={handleOpenLinkDialog}
                        sx={{ borderRadius: "8px" }}
                      >
                        <LinkRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleBodyInput}
                    onFocus={() => setLastFocused("body")}
                    sx={{
                      height: 340,
                      maxHeight: 340,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      outline: "none",
                      "&:focus": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                      },
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      "& ul, & ol": {
                        paddingLeft: "1.5rem",
                      },
                      "& li": {
                        marginBottom: "0.25rem",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Placeholders
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      height: 340,
                      maxHeight: 340,
                      overflowY: "auto",
                    }}
                  >
                    {[
                      {
                        group: "Client",
                        items: [
                          { label: "Name", placeholder: "{ClientName}" },
                          { label: "Address", placeholder: "{ClientAddress}" },
                        ],
                      },
                      {
                        group: "Linguist",
                        items: [
                          { label: "Name", placeholder: "{LinguistName}" },
                          { label: "Number", placeholder: "{LinguistNumber}" },
                          {
                            label: "Address",
                            placeholder: "{LinguistAddress}",
                          },
                        ],
                      },
                      {
                        group: "Assignment",
                        items: [
                          {
                            label: "Name",
                            placeholder: "{AssignmentName}",
                          },
                          {
                            label: "Language",
                            placeholder: "{AssignmentLanguage}",
                          },
                          {
                            label: "Type",
                            placeholder: "{AssignmentType}",
                          },
                          {
                            label: "Start Time",
                            placeholder: "{AssignmentStartTime}",
                          },
                          {
                            label: "End Time",
                            placeholder: "{AssignmentEndTime}",
                          },
                          {
                            label: "Duration",
                            placeholder: "{AssignmentDuration}",
                          },
                          { label: "Date", placeholder: "{AssignmentDate}" },
                          {
                            label: "Details",
                            placeholder: "{AssignmentDetails}",
                          },
                          {
                            label: "Address/Link",
                            placeholder: "{AssignmentAddressLink}",
                          },
                          {
                            label: "Requestor",
                            placeholder: "{AssignmentRequestor}",
                          },
                          {
                            label: "Contact Person",
                            placeholder: "{AssignmentContactPerson}",
                          },
                          {
                            label: "Contact Number",
                            placeholder: "{AssignmentContactPersonNumber}",
                          },
                          {
                            label: "Prep Time",
                            placeholder: "{AssignmentPrepTime}",
                          },
                          {
                            label: "Number of Assignments",
                            placeholder: "{NumberOfAssignments}",
                          },
                          {
                            label: "Assignments List",
                            placeholder: "{AssignmentsList}",
                          },
                        ],
                      },
                    ].map((group) => (
                      <Box
                        key={group.group}
                        sx={{
                          border: `1px solid ${theme.palette.primary.border2}`,
                          borderRadius: "10px",
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: 0.5 }}
                        >
                          {group.group}
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {group.items.map((item) => (
                            <Button
                              key={item.placeholder}
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                insertPlaceholder(item.placeholder)
                              }
                              sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                padding: "2px 8px",
                                minWidth: 0,
                              }}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Template is active"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default CommunicationTemplateNewEdit;
